/**
 * Where a page lives: "Hans / … / Vibe coding / VS Code". Every part opens its
 * page; with a deep path the middle folds into "…", which lists them.
 */
import { useTranslation } from 'react-i18next';
import { DropdownMenu } from 'radix-ui';
import type { PageRecord } from '@nexus/store';
import { useCatalog } from '../state/catalog';
import { useUi } from '../state/ui';
import { PageIcon } from './PageIcon';

const MAX_SHOWN = 3;

export function Breadcrumb({ page }: { page: PageRecord }) {
  const { t } = useTranslation();
  const byId = useCatalog((s) => s.byId);
  const open = useUi((s) => s.open);
  const ancestors: PageRecord[] = [];
  for (let p = page.parentId ? byId.get(page.parentId) : undefined, i = 0; p && i < 50; i++) {
    ancestors.unshift(p);
    p = p.parentId ? byId.get(p.parentId) : undefined;
  }
  if (ancestors.length === 0) return null;

  const folded = ancestors.length > MAX_SHOWN ? ancestors.slice(1, -2) : [];
  const shown = folded.length > 0 ? [ancestors[0]!, null, ...ancestors.slice(-2)] : ancestors;
  const name = (p: PageRecord) => p.title || t('page.untitled');
  const link =
    'max-w-48 truncate rounded px-1 py-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg';

  return (
    <nav
      aria-label={t('page.breadcrumb')}
      className="mb-2 flex min-w-0 flex-wrap items-center text-sm"
    >
      {shown.map((p, i) => (
        <span key={p?.id ?? 'more'} className="flex min-w-0 items-center">
          {p ? (
            <button
              type="button"
              className={link}
              onClick={() => open({ kind: 'page', pageId: p.id })}
            >
              {p.icon && (
                <>
                  <PageIcon icon={p.icon} />{' '}
                </>
              )}
              {name(p)}
            </button>
          ) : (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button type="button" className={link} aria-label={t('page.breadcrumbMore')}>
                  …
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={4}
                  className="z-[70] min-w-48 rounded-xl border border-border bg-bg p-1 text-sm shadow-pop"
                >
                  {folded.map((f) => (
                    <DropdownMenu.Item
                      key={f.id}
                      onSelect={() => open({ kind: 'page', pageId: f.id })}
                      className="cursor-pointer rounded px-2 py-1 outline-none data-[highlighted]:bg-bg-hover"
                    >
                      {name(f)}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
          <span aria-hidden className="px-0.5 text-fg-muted">
            /
          </span>
          {i === shown.length - 1 && (
            <span className="max-w-56 truncate px-1 text-fg" aria-current="page">
              {name(page)}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}
